import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Divider, FormControl, IconButton,
  InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LookupsApi } from './api';
import type { CalcType, Calculation, LookupSummary } from './types';
import {
  CALC_LABELS,
  ConditionalForm, ExpressionForm, LookupForm, SqlForm,
  buildConditional, buildLookup, parseConditional, parseLookup,
} from './CalcForms';

interface Props {
  calculations: Calculation[];
  onChange: (next: Calculation[]) => void;
  availableColumns: string[];
}

function CalcCard({
  calc, index, onChange, onDelete, onMoveUp, onMoveDown,
  isFirst, isLast, availableColumns, lookups, onLookupSaved,
}: {
  calc: Calculation;
  index: number;
  onChange: (next: Calculation) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  availableColumns: string[];
  lookups: LookupSummary[];
  onLookupSaved: () => void;
}) {
  const setType = (t: CalcType) => {
    onChange({ ...calc, calc_type: t, calc_expression: '' });
  };

  const cond = useMemo(() => parseConditional(calc.calc_expression), [calc.calc_expression]);
  const lk = useMemo(() => parseLookup(calc.calc_expression), [calc.calc_expression]);

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>#{index + 1}</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type" value={calc.calc_type}
              onChange={(e) => setType(e.target.value as CalcType)}
            >
              {(Object.keys(CALC_LABELS) as CalcType[]).map((t) => (
                <MenuItem key={t} value={t}>{CALC_LABELS[t]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="New column name"
            placeholder="e.g. commission_amount"
            size="small"
            value={calc.col_name}
            onChange={(e) => onChange({ ...calc, col_name: e.target.value })}
            sx={{ flexGrow: 1 }}
          />

          <Box sx={{ flexGrow: 0 }} />
          <Tooltip title="Move up"><span>
            <IconButton size="small" onClick={onMoveUp} disabled={isFirst}>
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </span></Tooltip>
          <Tooltip title="Move down"><span>
            <IconButton size="small" onClick={onMoveDown} disabled={isLast}>
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </span></Tooltip>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider sx={{ mb: 1.5 }} />

        {calc.calc_type === 'E' && (
          <ExpressionForm
            value={calc.calc_expression}
            onChange={(expr) => onChange({ ...calc, calc_expression: expr })}
            colOptions={availableColumns}
          />
        )}

        {calc.calc_type === 'C' && (
          <ConditionalForm
            condition={cond.condition} trueExpr={cond.trueExpr} falseExpr={cond.falseExpr}
            colOptions={availableColumns}
            onChange={(c, t, f) => onChange({ ...calc, calc_expression: buildConditional(c, t, f) })}
          />
        )}

        {calc.calc_type === 'L' && (
          <LookupForm
            value={lk}
            colOptions={availableColumns}
            lookups={lookups}
            onChange={(next) => onChange({
              ...calc,
              calc_expression: buildLookup(next.lookupTable, next.isDynamic, next.keyColumn, next.dateColumn),
            })}
            onLookupSaved={onLookupSaved}
          />
        )}

        {calc.calc_type === 'S' && (
          <SqlForm
            value={calc.calc_expression}
            onChange={(expr) => onChange({ ...calc, calc_expression: expr })}
            colOptions={availableColumns}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function CalculationGrid({ calculations, onChange, availableColumns }: Props) {
  const [lookups, setLookups] = useState<LookupSummary[]>([]);

  useEffect(() => {
    LookupsApi.list().then(setLookups).catch(() => setLookups([]));
  }, []);

  const setRow = (i: number, next: Calculation) => {
    const a = calculations.slice();
    a[i] = next;
    onChange(a);
  };
  const addRow = () => onChange([
    ...calculations,
    { col_name: '', calc_type: 'E', calc_expression: '' },
  ]);
  const removeRow = (i: number) => {
    const a = calculations.slice(); a.splice(i, 1); onChange(a);
  };
  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= calculations.length) return;
    const a = calculations.slice();
    [a[i], a[j]] = [a[j], a[i]];
    onChange(a);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="subtitle1">Calculations</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Each row adds a new column. Calculations run top-to-bottom and can use columns added above.
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addRow}>
          Add calculation
        </Button>
      </Stack>

      {calculations.map((c, i) => (
        <CalcCard
          key={i}
          calc={c} index={i}
          onChange={(n) => setRow(i, n)}
          onDelete={() => removeRow(i)}
          onMoveUp={() => move(i, -1)}
          onMoveDown={() => move(i, +1)}
          isFirst={i === 0}
          isLast={i === calculations.length - 1}
          availableColumns={availableColumns}
          lookups={lookups}
          onLookupSaved={() => {
            LookupsApi.list().then(setLookups).catch(() => undefined);
          }}
        />
      ))}

      {calculations.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No calculations yet. Click <strong>Add calculation</strong> to define one.
        </Box>
      )}
    </Box>
  );
}
